import React, { useState, useEffect, useCallback, useRef } from 'react';
import { rewardAPI, userAPI } from '../../../services';
import { Trophy, Plus, Star, IndianRupee, X, Trash2, RefreshCw, Search, ChevronDown } from 'lucide-react';

const CATEGORIES = [
  { v:'employee_of_month', l:'Employee of the Month' }, { v:'best_team_player', l:'Best Team Player' },
  { v:'innovation', l:'Innovation Award' }, { v:'customer_delight', l:'Customer Delight' },
  { v:'leadership', l:'Leadership Award' }, { v:'above_and_beyond', l:'Above & Beyond' },
  { v:'spot_award', l:'Spot Award' }, { v:'anniversary', l:'Work Anniversary' }, { v:'other', l:'Other' }
];

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const YEAR = new Date().getFullYear();

const EMPTY_FORM = { employeeId:'', title:'', description:'', category:'spot_award', points:0, cashAmount:0, month:new Date().getMonth()+1, year:YEAR, isPublic:true };

const RewardsPage = () => {
  const [rewards, setRewards] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [tab, setTab] = useState('rewards');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Employee picker state
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [empSearch, setEmpSearch] = useState('');
  const [showEmpList, setShowEmpList] = useState(false);
  const empPickerRef = useRef(null);

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [rr, lb] = await Promise.all([rewardAPI.getRewards(), rewardAPI.getLeaderboard(YEAR)]);
      setRewards(Array.isArray(rr?.data) ? rr.data : []);
      setLeaderboard(Array.isArray(lb?.data) ? lb.data : []);
    } catch { showToast('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Close emp picker on outside click
  useEffect(() => {
    const handler = (e) => { if (empPickerRef.current && !empPickerRef.current.contains(e.target)) setShowEmpList(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadEmployees = async () => {
    try {
      const res = await userAPI.getUsers({ limit: 200, active: true });
      const arr = res?.data?.users || res?.data || [];
      setEmployees(Array.isArray(arr) ? arr : []);
    } catch { /* ignore */ }
  };

  const openForm = () => {
    setForm(EMPTY_FORM);
    setSelectedEmployee(null);
    setEmpSearch('');
    setShowEmpList(false);
    setShowForm(true);
    loadEmployees();
  };

  const selectEmployee = (emp) => {
    setSelectedEmployee(emp);
    setForm(f => ({ ...f, employeeId: emp._id }));
    setEmpSearch('');
    setShowEmpList(false);
  };

  const clearEmployee = () => {
    setSelectedEmployee(null);
    setForm(f => ({ ...f, employeeId: '' }));
    setEmpSearch('');
  };

  const filteredEmployees = employees.filter(e => {
    const q = empSearch.toLowerCase();
    return !q || `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) || (e.employeeId || '').toLowerCase().includes(q) || (e.department || '').toLowerCase().includes(q);
  });

  const handleSave = async () => {
    if (!form.employeeId || !form.title.trim()) return showToast('Employee and title are required', 'error');
    try {
      setSaving(true);
      await rewardAPI.createReward({ ...form, employee: form.employeeId });
      showToast('Reward created');
      setShowForm(false); load();
    } catch { showToast('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reward?')) return;
    try { await rewardAPI.deleteReward(id); showToast('Deleted'); load(); }
    catch { showToast('Failed', 'error'); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type==='error'?'bg-red-500':'bg-green-500'}`}>{toast.msg}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Trophy className="w-6 h-6 text-yellow-500"/>Rewards & Recognition</h1>
          <p className="text-sm text-gray-500 mt-1">Recognise and celebrate employee achievements</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"><RefreshCw className="w-4 h-4"/></button>
          <button onClick={openForm} className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600"><Plus className="w-4 h-4"/>Give Award</button>
        </div>
      </div>

      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit">
        {[['rewards','Awards'],['leaderboard','Leaderboard']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab===k?'bg-white shadow text-yellow-600':'text-gray-600 hover:text-gray-900'}`}>{l}</button>
        ))}
      </div>

      {tab === 'rewards' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? <p className="col-span-3 text-center py-10 text-gray-400">Loading...</p>
          : rewards.length === 0 ? (
            <div className="col-span-3 flex flex-col items-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200">
              <Trophy className="w-12 h-12 mb-3 opacity-30"/><p>No awards yet. Give your first award!</p>
            </div>
          ) : rewards.map(r => (
            <div key={r._id} className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border border-yellow-200 p-5 relative">
              <button onClick={() => handleDelete(r._id)} className="absolute top-3 right-3 p-1 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4"/></button>
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-6 h-6 text-yellow-500"/>
                <span className="text-xs font-semibold bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">{CATEGORIES.find(c=>c.v===r.category)?.l || r.category}</span>
              </div>
              <p className="font-bold text-gray-900 text-lg">{r.employee?.firstName} {r.employee?.lastName}</p>
              <p className="text-xs text-gray-500 mb-2">{r.employee?.employeeId} · {r.employee?.department}</p>
              <p className="text-sm text-gray-700 mb-3">{r.title}</p>
              <div className="flex gap-3">
                {r.points > 0 && <div className="flex items-center gap-1 text-yellow-700 text-sm font-semibold"><Star className="w-4 h-4"/>{r.points} pts</div>}
                {r.cashAmount > 0 && <div className="flex items-center gap-1 text-green-700 text-sm font-semibold"><IndianRupee className="w-4 h-4"/>₹{r.cashAmount}</div>}
              </div>
              <p className="text-xs text-gray-400 mt-2">{MONTH_NAMES[(r.month||1)-1]} {r.year} · By {r.nominatedBy?.firstName}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'leaderboard' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 font-semibold text-gray-800 flex items-center gap-2"><Star className="w-4 h-4 text-yellow-500"/>Leaderboard — {YEAR}</div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{['Rank','Employee','Department','Awards','Points','Cash'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {leaderboard.map((e, i) => (
                <tr key={e._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-bold ${i===0?'bg-yellow-400 text-white':i===1?'bg-gray-300 text-gray-800':i===2?'bg-orange-300 text-white':'bg-gray-100 text-gray-700'}`}>{i+1}</span>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{e.employee?.firstName} {e.employee?.lastName}</td>
                  <td className="px-4 py-3 text-gray-500">{e.employee?.department || '—'}</td>
                  <td className="px-4 py-3 text-gray-700 font-semibold">{e.awardCount}</td>
                  <td className="px-4 py-3 text-yellow-700 font-bold">{e.totalPoints}</td>
                  <td className="px-4 py-3 text-green-700 font-bold">₹{e.totalCash?.toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Award Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Give Award</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-4">

              {/* Employee Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                {selectedEmployee ? (
                  <div className="flex items-center gap-2 px-3 py-2 border border-blue-300 bg-blue-50 rounded-lg">
                    <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {selectedEmployee.firstName?.[0]}{selectedEmployee.lastName?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{selectedEmployee.firstName} {selectedEmployee.lastName}</p>
                      <p className="text-xs text-gray-500">{selectedEmployee.employeeId} · {selectedEmployee.department}</p>
                    </div>
                    <button onClick={clearEmployee} className="text-gray-400 hover:text-red-500 flex-shrink-0"><X className="w-4 h-4"/></button>
                  </div>
                ) : (
                  <div ref={empPickerRef} className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                      <input
                        value={empSearch}
                        onChange={e => { setEmpSearch(e.target.value); setShowEmpList(true); }}
                        onFocus={() => setShowEmpList(true)}
                        placeholder="Search by name, ID or department..."
                        className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                    </div>
                    {showEmpList && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
                        {filteredEmployees.length === 0 ? (
                          <p className="px-4 py-3 text-sm text-gray-400">No employees found</p>
                        ) : filteredEmployees.map(emp => (
                          <button
                            key={emp._id}
                            onClick={() => selectEmployee(emp)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 text-left transition-colors"
                          >
                            <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {emp.firstName?.[0]}{emp.lastName?.[0]}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{emp.firstName} {emp.lastName}</p>
                              <p className="text-xs text-gray-500">{emp.employeeId}{emp.department ? ` · ${emp.department}` : ''}</p>
                            </div>
                            {emp.designation && <span className="text-xs text-gray-400 truncate max-w-24">{emp.designation}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div><label className="block text-sm font-medium text-gray-700 mb-1">Award Title *</label>
                <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Outstanding Performance in Q2"/></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  {CATEGORIES.map(c=><option key={c.v} value={c.v}>{c.l}</option>)}
                </select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Why are they being recognised?"/></div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
                  <input type="number" min="0" value={form.points} onChange={e=>setForm(f=>({...f,points:parseInt(e.target.value)||0}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Cash (₹)</label>
                  <input type="number" min="0" value={form.cashAmount} onChange={e=>setForm(f=>({...f,cashAmount:parseInt(e.target.value)||0}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select value={form.month} onChange={e=>setForm(f=>({...f,month:parseInt(e.target.value)}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {MONTH_NAMES.map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
                  </select></div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="flex-1 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 disabled:opacity-60">{saving?'Saving...':'Give Award'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardsPage;
